import React, { useState, useEffect, useCallback } from "react";
import { Badge, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import WaitlistService from "../../../services/waitlist_service";
import { useTenant } from "../../../hooks/useTenant";

/**
 * WaitlistPanel
 * Painel lateral (sidebar) da lista de espera, exibido ao lado do AttendanceModal.
 * Mostra os alunos na fila de espera da turma com posição, foto, nome e link para perfil.
 * A remoção da lista é feita exclusivamente pelo perfil do membro.
 */
const WaitlistPanel = ({ isOpen, onClose, schedule }) => {
    const { idTenant } = useTenant();
    const navigate = useNavigate();
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);

    const idClass = schedule?.idClass;

    const fetchWaitlist = useCallback(async () => {
        if (!idTenant || !idClass) return;

        setLoading(true);
        try {
            const data = await WaitlistService.getClassWaitlist(idTenant, idClass);
            setEntries(data);
        } catch (error) {
            console.error("[WaitlistPanel] Erro ao buscar lista:", error);
            toast.error("Erro ao carregar lista de espera.");
        } finally {
            setLoading(false);
        }
    }, [idTenant, idClass]);

    useEffect(() => {
        if (isOpen && idClass) {
            fetchWaitlist();
        }
    }, [isOpen, idClass, fetchWaitlist]);

    const handleGoToProfile = (idMember) => {
        if (idMember) {
            navigate(`/members/${idMember}`);
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="d-flex flex-column bg-white border-start"
            style={{
                width: "380px",
                minWidth: "380px",
                height: "100%",
                overflow: "hidden",
            }}
        >
            {/* Header */}
            <div className="px-3 py-3 border-bottom d-flex align-items-center justify-content-between"
                style={{ background: "rgba(var(--bs-warning-rgb), 0.08)" }}
            >
                <div className="d-flex align-items-center gap-2">
                    <i className="mdi mdi-account-clock-outline text-warning fs-4"></i>
                    <div>
                        <h6 className="mb-0 fw-bold font-size-14">Lista de Espera</h6>
                        <small className="text-muted font-size-11">
                            {schedule?.activityName}
                        </small>
                    </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <Badge color="warning" className="font-size-11 px-2" pill>
                        {entries.length}
                    </Badge>
                    <button
                        className="btn btn-light btn-sm btn-icon rounded-1"
                        onClick={onClose}
                        style={{ width: "28px", height: "28px", padding: 0 }}
                    >
                        <i className="mdi mdi-close fs-6"></i>
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="flex-grow-1 p-3" style={{ overflowY: "auto" }}>
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner color="warning" size="sm" className="mb-2" />
                        <p className="text-muted mb-0 font-size-12">Carregando...</p>
                    </div>
                ) : entries.length === 0 ? (
                    <div className="text-center py-5">
                        <i className="mdi mdi-account-clock text-muted" style={{ fontSize: "40px" }}></i>
                        <p className="text-muted mt-2 mb-0 font-size-12">Nenhum aluno na lista de espera.</p>
                    </div>
                ) : (
                    <div className="d-flex flex-column gap-2">
                        {entries.map((entry, index) => (
                            <div
                                key={entry.id}
                                className="d-flex align-items-center bg-white rounded-2 px-2 py-2"
                                style={{
                                    border: "1px solid rgba(0,0,0,0.06)",
                                }}
                            >
                                {/* Position */}
                                <span className="text-muted fw-bold font-size-13 me-2 flex-shrink-0" style={{ minWidth: "22px" }}>
                                    {entry.position}-
                                </span>

                                {/* Avatar */}
                                <div className="me-2 flex-shrink-0">
                                    {(entry.memberPhotoUrl || entry.memberPhoto) ? (
                                        <img
                                            src={entry.memberPhotoUrl || entry.memberPhoto}
                                            alt={entry.memberName}
                                            style={{
                                                width: "34px",
                                                height: "34px",
                                                borderRadius: "50%",
                                                objectFit: "cover",
                                                border: "1px solid rgba(0,0,0,0.08)",
                                            }}
                                        />
                                    ) : (
                                        <div
                                            className="bg-warning bg-opacity-10 text-warning d-flex align-items-center justify-content-center rounded-circle"
                                            style={{ width: "34px", height: "34px", fontSize: "13px", fontWeight: "bold" }}
                                        >
                                            {entry.memberName?.charAt(0) || "?"}
                                        </div>
                                    )}
                                </div>

                                {/* Name */}
                                <div className="flex-grow-1 fw-semibold text-dark font-size-13 text-truncate" style={{ minWidth: 0 }}>
                                    {entry.memberName || "Sem nome"}
                                </div>

                                {/* Ver Perfil */}
                                <button
                                    className="btn btn-light btn-sm p-0 d-flex align-items-center justify-content-center rounded-1 ms-2 flex-shrink-0"
                                    style={{ width: "28px", height: "28px" }}
                                    onClick={() => handleGoToProfile(entry.idMember)}
                                    title="Ver Perfil"
                                >
                                    <i className="mdi mdi-eye-outline text-primary font-size-14"></i>
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default WaitlistPanel;
